import React from "react";
import { useWorkStream } from "@/context/WorkStreamContext";

/**
 * CTO Directive #122.1.3 — Living Orb SSE hook + triple-status row.
 *
 * The Orb visual (ring, halo) is owned by <LivingOrb />; here we
 * render ONLY the verified Turkish label the backend sent, plus
 * three honest badges (Ses / Dinleme / Eylem) that reflect the
 * current SSE phase — never a local guess.
 */

// SSE phase → tone class for the outer ring.
const PHASE_TONE = {
  preview_ready: "border-slate-400/40 text-slate-200",
  speaking_preview: "border-sky-400/60 text-sky-200",
  listening_for_confirmation: "border-indigo-400/60 text-indigo-200",
  command_understood: "border-teal-400/60 text-teal-200",
  applying: "border-amber-400/60 text-amber-200",
  reverifying: "border-amber-300/50 text-amber-100",
  completed: "border-emerald-400/60 text-emerald-200",
  cancelled: "border-slate-500/40 text-slate-400",
  failed_honestly: "border-rose-400/30 text-rose-200",
};


function Triple({ label, active, tone, testId }) {
  return (
    <span
      data-testid={testId}
      data-active={active ? "1" : "0"}
      className={
        "mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border " +
        (active
          ? "border-sky-300/50 text-sky-100"
          : "border-slate-700/50 text-slate-500")
      }
      title={tone}
    >
      {label}
    </span>
  );
}


export default function WorkStreamOrb({ language = "tr" }) {
  const { state } = useWorkStream();
  if (!state.phase) return null;

  const tone = PHASE_TONE[state.phase] || "border-slate-500/40 text-slate-300";

  // CTO §"Ses / Dinleme / Eylem" — three honest badges.
  const speakingPhases = new Set([
    "speaking_preview", "completed", "reverifying",
  ]);
  const listeningPhases = new Set([
    "listening_for_confirmation",
  ]);
  const actionPhases = new Set([
    "applying", "reverifying", "completed",
  ]);
  const ses = speakingPhases.has(state.phase);
  const din = listeningPhases.has(state.phase);
  const eyl = actionPhases.has(state.phase);
  const eylReal = eyl && state.verificationMode === "real"
                    && state.phase === "completed" && state.verified;
  const eylSim = eyl && !eylReal;

  return (
    <div
      className="mt-3 flex flex-col items-center gap-1"
      data-testid="me-work-stream-orb"
      data-phase={state.phase}
      data-verification-mode={state.verificationMode}
    >
      <span
        className={`mono text-[11px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full border ${tone}`}
        data-testid="work-stream-orb-label"
      >
        {state.orbLabelTr || (language === "tr" ? "…" : "…")}
      </span>
      {state.textTr && (
        <span
          className="text-[10px] italic text-slate-400 max-w-[280px] text-center"
          data-testid="work-stream-orb-text"
        >
          {state.textTr}
        </span>
      )}
      <div className="mt-1 flex items-center gap-2">
        <Triple
          label={language === "tr" ? (ses ? "SES: HAZIR" : "SES: BEKLİYOR") : (ses ? "AUDIO ON" : "AUDIO IDLE")}
          active={ses}
          tone={ses ? "audio verified" : "audio idle"}
          testId="work-stream-audio"
        />
        <Triple
          label={language === "tr" ? (din ? "DİNLEME: AÇIK" : "DİNLEME: KAPALI") : (din ? "MIC ON" : "MIC OFF")}
          active={din}
          tone={din ? "mic listening" : "mic closed"}
          testId="work-stream-listen"
        />
        <Triple
          label={
            language === "tr"
              ? (eylReal ? "EYLEM: GERÇEK" : (eylSim ? "EYLEM: SİMÜLASYON" : "EYLEM: —"))
              : (eylReal ? "ACTION: REAL" : (eylSim ? "ACTION: SIM" : "ACTION: —"))
          }
          active={eylReal}
          tone={eylReal ? "real Windows apply" : "simulated"}
          testId="work-stream-action"
        />
      </div>
    </div>
  );
}
